import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, Star, Mail, FlaskConical } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/hooks/useAuth";

interface EligibleBooking {
  id: string;
  booking_number: string;
  customer_name: string;
  customer_email: string;
  user_id: string;
  provider_id: string;
  provider_name: string;
  booking_date: string;
  checked_out_at: string;
  created_at: string;
}

const APP_URL = "https://wellsuites.co";

const ReviewRequestsManagement = () => {
  const { user } = useAuth();
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [sendingAll, setSendingAll] = useState(false);
  const [testMode, setTestMode] = useState(false);

  const { data: eligible = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["admin-review-eligible-bookings"],
    queryFn: async (): Promise<EligibleBooking[]> => {
      // Last 90 days window
      const since = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString();

      // 1. Completed, checked-out bookings
      const { data: bookings, error: bErr } = await supabase
        .from("bookings")
        .select(
          "id, booking_number, customer_name, customer_email, user_id, provider_id, booking_date, checked_out_at, created_at, providers(name)"
        )
        .eq("status", "completed")
        .not("checked_out_at", "is", null)
        .gte("checked_out_at", since)
        .order("checked_out_at", { ascending: false })
        .limit(500);

      if (bErr) throw bErr;
      if (!bookings || bookings.length === 0) return [];

      const userIds = Array.from(new Set(bookings.map((b) => b.user_id)));
      const providerIds = Array.from(new Set(bookings.map((b) => b.provider_id)));

      // 2. Find each user's FIRST booking (oldest by created_at) to ensure we only consider that one
      const { data: firstBookings, error: fbErr } = await supabase
        .from("bookings")
        .select("id, user_id, created_at")
        .in("user_id", userIds)
        .order("created_at", { ascending: true });

      if (fbErr) throw fbErr;

      const firstBookingByUser = new Map<string, string>();
      for (const fb of firstBookings || []) {
        if (!firstBookingByUser.has(fb.user_id)) {
          firstBookingByUser.set(fb.user_id, fb.id);
        }
      }

      // 3. Email logs for review-request
      const { data: logs, error: lErr } = await supabase
        .from("email_logs")
        .select("metadata")
        .eq("template_name", "review-request");

      if (lErr) throw lErr;

      const loggedBookingIds = new Set<string>();
      for (const l of logs || []) {
        const meta = l.metadata as any;
        if (meta && meta.booking_id) loggedBookingIds.add(meta.booking_id);
      }

      // 4. Existing reviews: any review by user for provider
      const { data: reviews, error: rErr } = await (supabase as any)
        .from("reviews")
        .select("user_id, provider_id")
        .in("user_id", userIds)
        .in("provider_id", providerIds);

      if (rErr) throw rErr;

      const reviewedPairs = new Set<string>();
      for (const r of reviews || []) {
        reviewedPairs.add(`${r.user_id}:${r.provider_id}`);
      }

      // 5. Filter
      return bookings
        .filter((b) => firstBookingByUser.get(b.user_id) === b.id)
        .filter((b) => !loggedBookingIds.has(b.id))
        .filter((b) => !reviewedPairs.has(`${b.user_id}:${b.provider_id}`))
        .map((b) => ({
          id: b.id,
          booking_number: b.booking_number,
          customer_name: b.customer_name,
          customer_email: b.customer_email,
          user_id: b.user_id,
          provider_id: b.provider_id,
          provider_name: (b.providers as any)?.name || "Unknown",
          booking_date: b.booking_date,
          checked_out_at: b.checked_out_at!,
          created_at: b.created_at,
        }));
    },
  });

  const sendOne = async (b: EligibleBooking): Promise<boolean> => {
    setSendingIds((s) => new Set(s).add(b.id));
    try {
      const recipientEmail = testMode && user?.email ? user.email : b.customer_email;
      const recipientName = testMode && user?.email
        ? `[TEST] ${b.customer_name}`
        : b.customer_name;

      // Create or reuse token via secure edge function (service role).
      // Use direct fetch so we can read the JSON error body on non-2xx responses.
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error("Not authenticated");

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

      const tokenRes = await fetch(`${supabaseUrl}/functions/v1/create-review-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          apikey: anonKey,
        },
        body: JSON.stringify({ booking_id: b.id }),
      });
      const tokenData = await tokenRes.json().catch(() => ({} as any));
      if (!tokenRes.ok) {
        const msg = tokenData?.error || tokenData?.details || `HTTP ${tokenRes.status}`;
        throw new Error(msg);
      }
      const token = tokenData?.token;
      if (!token) throw new Error("Failed to create review token");

      const reviewUrl = `${APP_URL}/review/${token}`;

      const { error } = await supabase.functions.invoke(
        "send-mailersend-transactional",
        {
          body: {
            template: "review-request",
            to: { email: recipientEmail, name: recipientName },
            variables: {
              provider_name: b.provider_name,
              review_url: reviewUrl,
            },
            metadata: {
              booking_id: b.id,
              provider_id: b.provider_id,
              user_id: b.user_id,
              test_mode: testMode,
              original_recipient: testMode ? b.customer_email : undefined,
            },
          },
        }
      );
      if (error) throw error;
      return true;
    } catch (e: any) {
      console.error("Failed to send review request", e);
      toast.error(`Failed for ${b.customer_email}: ${e.message || e}`);
      return false;
    } finally {
      setSendingIds((s) => {
        const next = new Set(s);
        next.delete(b.id);
        return next;
      });
    }
  };

  const handleSendOne = async (b: EligibleBooking) => {
    const ok = await sendOne(b);
    if (ok) {
      toast.success(
        testMode
          ? `Test email sent to ${user?.email} (would go to ${b.customer_email})`
          : `Review request sent to ${b.customer_email}`
      );
      refetch();
    }
  };

  const handleSendAll = async () => {
    if (eligible.length === 0) return;
    setSendingAll(true);
    let sent = 0;
    let failed = 0;
    for (const b of eligible) {
      const ok = await sendOne(b);
      if (ok) sent++;
      else failed++;
      // small delay to be gentle on the function
      await new Promise((r) => setTimeout(r, 300));
    }
    setSendingAll(false);
    toast.success(`Sent ${sent} review request${sent === 1 ? "" : "s"}${failed ? `, ${failed} failed` : ""}`);
    refetch();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Review Requests
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Customers who completed their first booking but haven't received a review request or left a review yet.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/30">
              <FlaskConical className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="test-mode" className="text-sm cursor-pointer">
                Test mode
              </Label>
              <Switch
                id="test-mode"
                checked={testMode}
                onCheckedChange={setTestMode}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={handleSendAll}
                disabled={sendingAll || eligible.length === 0}
              >
                {sendingAll ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Send All ({eligible.length})
              </Button>
            </div>
          </div>
        </div>
        {testMode && (
          <div className="mt-3 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-200">
            Test mode is ON. All emails will be sent to <strong>{user?.email}</strong> instead of customers.
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : eligible.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Star className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No eligible customers</p>
            <p className="text-sm mt-1">Everyone who's checked out has already been asked or left a review.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Checked out</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eligible.map((b) => {
                  const sending = sendingIds.has(b.id);
                  return (
                    <TableRow key={b.id}>
                      <TableCell>
                        <div className="font-medium">{b.customer_name}</div>
                        <div className="text-xs text-muted-foreground">{b.customer_email}</div>
                      </TableCell>
                      <TableCell>{b.provider_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {b.booking_number}
                        </Badge>
                        <div className="text-xs text-muted-foreground mt-1">
                          {new Date(b.booking_date).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(b.checked_out_at), { addSuffix: true })}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendOne(b)}
                          disabled={sending || sendingAll}
                        >
                          {sending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Send Request
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReviewRequestsManagement;
